import React from 'react';
import { Box, Stack, Typography, Avatar, Rating, Button } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';

// Keyframe animations
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(180deg); }
`;

const sparkle = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.2); opacity: 0.8; }
`;

const glow = keyframes`
  0% { opacity: 0.5; transform: scaleX(0.8); }
  100% { opacity: 1; transform: scaleX(1); }
`;

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
`;

const floatShape = keyframes`
  0%, 100% { transform: rotate(45deg) translateY(0); }
  50% { transform: rotate(45deg) translateY(-10px); }
`;

const defaultAvatarImgProps = {
  crossOrigin: 'anonymous',
  referrerPolicy: 'no-referrer'
};

const PreviewContainer = styled(Box, {
  shouldForwardProp: (prop) => !['templateStyle', 'layout'].includes(prop)
})(({ theme, templateStyle, layout }) => {
  const baseStyles = {
    width: '100%',
    maxWidth: '716px',
    height: 'auto',
    minHeight: '350px',
    aspectRatio: '716 / 500',
    background: templateStyle?.background || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '0px',
    padding: layout === 'full-width' ? '16px' : '24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    marginBottom: '16px',
    boxShadow: templateStyle?.shadow || '0 10px 20px rgba(102, 126, 234, 0.2)',
    border: templateStyle?.border || 'none',
    backdropFilter: templateStyle?.backdrop || 'none',
    overflow: 'hidden',
    [theme.breakpoints.up('sm')]: {
      padding: layout === 'full-width' ? '24px' : '32px',
      marginBottom: '20px',
      boxShadow: templateStyle?.shadow || '0 15px 30px rgba(102, 126, 234, 0.25)'
    },
    [theme.breakpoints.up('md')]: {
      padding: layout === 'full-width' ? '20px' : '40px',
      marginBottom: '24px',
      boxShadow: templateStyle?.shadow || '0 20px 40px rgba(102, 126, 234, 0.3)'
    }
  };

  if (layout === 'geometric-modern') {
    return {
      ...baseStyles,
      background: templateStyle?.background,
      backdropFilter: templateStyle?.backdropFilter,
      border: templateStyle?.border,
      transform: templateStyle?.transform,
      transition: 'all 0.5s ease',
      '&:hover': {
        transform: 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1.02)'
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: templateStyle?.radialBackground,
        pointerEvents: 'none'
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: templateStyle?.gradientOverlay,
        borderRadius: templateStyle?.borderRadius,
        pointerEvents: 'none'
      }
    };
  }

  if (layout === 'creative-geometric') {
    return {
      ...baseStyles,
      background: templateStyle?.background,
      padding: '2rem',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: templateStyle?.radialBackground,
        zIndex: -1
      }
    };
  }

  if (layout === 'professional-blue') {
    return {
      ...baseStyles,
      background: templateStyle?.background,
      padding: '40px',
      position: 'relative',
      backgroundSize: '50px 50px',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: templateStyle?.backgroundPattern,
        backgroundSize: '50px 50px',
        opacity: 0.5,
        zIndex: 0
      }
    };
  }

  if (layout === 'orange-accent') {
    return {
      ...baseStyles,
      background: templateStyle?.background,
      padding: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: templateStyle?.backgroundPattern,
        zIndex: 0
      }
    };
  }

  return baseStyles;
});

// Client Feedback (Template 1) Components
const ClientFeedbackOuter = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'templateStyle'
})(({ theme, templateStyle }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: '716px',
  minHeight: 'auto',
  borderRadius: templateStyle?.containerRadius || '24px',
  background: templateStyle?.containerBackground || 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
  boxShadow: templateStyle?.containerShadow || '0 10px 30px rgba(0, 0, 0, 0.1)',
  padding: '24px 16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  fontFamily: templateStyle?.fontFamily || 'Inter, sans-serif',
  [theme.breakpoints.up('sm')]: {
    padding: '32px 24px',
    minHeight: '420px',
    borderRadius: templateStyle?.containerRadius || '36px',
    boxShadow: templateStyle?.containerShadow || '0 15px 40px rgba(0, 0, 0, 0.1)'
  },
  [theme.breakpoints.up('md')]: {
    padding: '40px',
    minHeight: '500px',
    borderRadius: templateStyle?.containerRadius || '48px',
    boxShadow: templateStyle?.containerShadow || '0 20px 50px rgba(0, 0, 0, 0.1)'
  }
}));

const ClientFeedbackBlob = styled(Box, {
  shouldForwardProp: (prop) => !['variant', 'templateStyle'].includes(prop)
})(({ variant, templateStyle }) => {
  const baseStyles = {
    position: 'absolute',
    width: '256px',
    height: '256px',
    borderRadius: '50%',
    filter: 'blur(80px)',
    zIndex: 1
  };

  if (variant === 'warm') {
    return {
      ...baseStyles,
      top: 0,
      left: 0,
      background: `radial-gradient(circle, ${templateStyle?.blobWarm || 'rgba(254, 215, 170, 0.3)'} 0%, transparent 70%)`
    };
  }

  return {
    ...baseStyles,
    bottom: 0,
    right: 0,
    background: `radial-gradient(circle, ${templateStyle?.blobCool || 'rgba(219, 234, 254, 0.3)'} 0%, transparent 70%)`
  };
});

const ClientFeedbackContent = styled(Stack)(({ theme }) => ({
  position: 'relative',
  zIndex: 10,
  width: '100%',
  maxWidth: '650px',
  textAlign: 'center',
  gap: '32px',
  [theme.breakpoints.up('sm')]: {
    gap: '48px'
  },
  [theme.breakpoints.up('md')]: {
    gap: '64px'
  }
}));

const ClientFeedbackTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'templateStyle'
})(({ theme, templateStyle }) => ({
  fontSize: '26px',
  fontWeight: templateStyle?.titleFontWeight || 300,
  color: templateStyle?.titleColor || '#1e293b',
  letterSpacing: '-0.5px',
  [theme.breakpoints.up('sm')]: {
    fontSize: '36px'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: templateStyle?.titleFontSize || '48px'
  }
}));

const ClientFeedbackHighlight = styled('span', {
  shouldForwardProp: (prop) => prop !== 'templateStyle'
})(({ templateStyle }) => ({
  fontWeight: 700,
  color: templateStyle?.highlightColor || '#f97316'
}));

const ClientFeedbackCardWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  margin: '0 auto'
}));

const ClientFeedbackAvatarWrapper = styled(Box, {
  shouldForwardProp: (prop) => !['templateStyle'].includes(prop)
})(({ templateStyle, theme }) => ({
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  top: '-40px',
  zIndex: 20,
  [theme.breakpoints.up('sm')]: {
    top: '-52px'
  },
  [theme.breakpoints.up('md')]: {
    top: '-64px'
  }
}));

const ClientFeedbackAvatar = styled(Box, {
  shouldForwardProp: (prop) => !['templateStyle'].includes(prop)
})(({ templateStyle, theme }) => ({
  width: '80px',
  height: '80px',
  borderRadius: '50%',
  background: templateStyle?.avatarGradient || 'linear-gradient(135deg, #cbd5e1 0%, #94a3b8 100%)',
  border: templateStyle?.avatarBorder || '5px solid #ffffff',
  boxShadow: templateStyle?.avatarShadow || '0 6px 20px rgba(0, 0, 0, 0.15)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '32px',
  fontWeight: 600,
  color: '#ffffff',
  overflow: 'hidden',
  [theme.breakpoints.up('sm')]: {
    width: '100px',
    height: '100px',
    fontSize: '40px',
    border: templateStyle?.avatarBorder || '6px solid #ffffff',
    boxShadow: templateStyle?.avatarShadow || '0 8px 25px rgba(0, 0, 0, 0.15)'
  },
  [theme.breakpoints.up('md')]: {
    width: templateStyle?.avatarSize || '128px',
    height: templateStyle?.avatarSize || '128px',
    fontSize: '48px',
    border: templateStyle?.avatarBorder || '8px solid #ffffff',
    boxShadow: templateStyle?.avatarShadow || '0 10px 30px rgba(0, 0, 0, 0.15)'
  },
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  }
}));

const ClientFeedbackCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'templateStyle'
})(({ theme, templateStyle }) => ({
  background: templateStyle?.cardBackground || 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
  borderRadius: templateStyle?.cardRadius || '24px',
  boxShadow: '0 12px 30px rgba(0, 0, 0, 0.2)',
  padding: templateStyle?.cardPadding || '50px 16px 32px',
  position: 'relative',
  overflow: 'hidden',
  minHeight: '280px',
  [theme.breakpoints.up('sm')]: {
    padding: templateStyle?.cardPadding || '65px 24px 40px',
    minHeight: '320px',
    borderRadius: templateStyle?.cardRadius || '36px'
  },
  [theme.breakpoints.up('md')]: {
    padding: templateStyle?.cardPadding || '80px 32px 48px',
    minHeight: '360px',
    borderRadius: templateStyle?.cardRadius || '48px',
    boxShadow: '0 20px 50px rgba(0, 0, 0, 0.2)'
  }
}));

const ClientFeedbackPattern = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'templateStyle'
})(({ templateStyle }) => ({
  position: 'absolute',
  inset: 0,
  opacity: templateStyle?.patternOpacity ?? 0.05,
  backgroundImage:
    'radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.1) 1px, transparent 1px)',
  backgroundSize: '24px 24px',
  zIndex: 1
}));

const ClientFeedbackCardContent = styled(Stack)(({ theme }) => ({
  position: 'relative',
  zIndex: 10,
  textAlign: 'center',
  gap: '16px'
}));

const ClientFeedbackVerified = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'templateStyle'
})(({ templateStyle }) => ({
  fontSize: '14px',
  color: templateStyle?.subtitleColor || '#cbd5e1',
  fontWeight: 500
}));

const ClientFeedbackText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'templateStyle'
})(({ templateStyle, theme }) => ({
  fontSize: '14px',
  color: templateStyle?.textColor || 'rgba(255, 255, 255, 0.9)',
  lineHeight: templateStyle?.feedbackLineHeight || 1.5,
  fontWeight: 400,
  marginBottom: '20px',
  [theme.breakpoints.up('sm')]: {
    fontSize: '16px',
    lineHeight: templateStyle?.feedbackLineHeight || 1.6,
    marginBottom: '28px'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: templateStyle?.feedbackFontSize || '18px',
    marginBottom: '32px'
  }
}));

const ClientFeedbackStars = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px'
}));

const ClientFeedbackStar = styled('span', {
  shouldForwardProp: (prop) => !['filled', 'templateStyle'].includes(prop)
})(({ filled, templateStyle }) => ({
  width: templateStyle?.starSize || '24px',
  height: templateStyle?.starSize || '24px',
  display: 'inline-flex',
  '& svg': {
    width: '100%',
    height: '100%',
    fill: filled ? (templateStyle?.starActive || '#fb923c') : (templateStyle?.starInactive || '#475569'),
    color: filled ? (templateStyle?.starActive || '#fb923c') : (templateStyle?.starInactive || '#475569')
  }
}));

const ContentCard = styled(Box, {
  shouldForwardProp: (prop) => !['templateStyle', 'layout'].includes(prop)
})(({ theme, templateStyle, layout }) => {
  const baseStyles = {
    backgroundColor: templateStyle?.cardBackground || '#ffffff',
    borderRadius: '0px',
    padding: layout === 'split-layout' ? '20px' : '24px',
    maxWidth: layout === 'full-width' ? '100%' : '500px',
    width: '100%',
    textAlign: layout === 'split-layout' ? 'left' : 'center',
    boxShadow: layout === 'minimalist' ? 'none' : '0px 6px 24px rgba(0, 0, 0, 0.10)',
    position: 'relative',
    [theme.breakpoints.up('sm')]: {
      padding: layout === 'split-layout' ? '28px' : '32px',
      boxShadow: layout === 'minimalist' ? 'none' : '0px 8px 32px rgba(0, 0, 0, 0.12)'
    }
  };

  if (layout === 'split-layout') {
    return {
      ...baseStyles,
      display: 'grid',
      gridTemplateColumns: '1fr 2fr',
      gap: '24px',
      alignItems: 'center',
      textAlign: 'left'
    };
  }

  if (layout === 'professional') {
    return {
      ...baseStyles,
      border: `2px solid ${templateStyle?.primaryColor}`,
      backgroundColor: templateStyle?.cardBackground || '#374151'
    };
  }

  if (layout === 'creative-geometric') {
    return {
      ...baseStyles,
      background: templateStyle?.cardBackground || 'rgba(17, 24, 39, 0.8)',
      backdropFilter: templateStyle?.backdrop || 'blur(20px)',
      border: templateStyle?.border || '1px solid rgba(75, 85, 99, 0.3)',
      borderRadius: '0px',
      padding: '1.5rem',
      position: 'relative',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      [theme.breakpoints.up('sm')]: {
        padding: '2rem'
      },
      [theme.breakpoints.up('md')]: {
        padding: '3rem',
        '&:hover': {
          transform: 'translateY(-8px)',
          borderColor: 'rgba(75, 85, 99, 0.5)',
          background: 'rgba(17, 24, 39, 0.9)'
        }
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: templateStyle?.cardGradient,
        borderRadius: '0px',
        zIndex: -1
      }
    };
  }

  return baseStyles;
});

const TitleSection = styled(Stack, {
  shouldForwardProp: (prop) => !['layout'].includes(prop)
})(({ theme, layout }) => ({
  alignItems: layout === 'split-layout' ? 'flex-start' : 'center',
  marginBottom: layout === 'full-width' ? '12px' : '16px',
  gap: '6px',
  gridColumn: layout === 'split-layout' ? '1 / -1' : 'auto',
  [theme.breakpoints.up('sm')]: {
    marginBottom: layout === 'full-width' ? '16px' : '20px',
    gap: '8px'
  },
  [theme.breakpoints.up('md')]: {
    marginBottom: layout === 'full-width' ? '16px' : '24px'
  }
}));

const MainTitle = styled(Typography, {
  shouldForwardProp: (prop) => !['templateStyle', 'layout'].includes(prop)
})(({ theme, templateStyle, layout }) => ({
  fontFamily: `${templateStyle?.fontFamily || 'Inter'}, sans-serif`,
  fontSize: '18px',
  fontWeight: layout === 'professional' ? 700 : layout === 'full-width' ? 800 : 300,
  color: layout === 'split-layout' ? templateStyle?.primaryColor : templateStyle?.primaryColor || '#667eea',
  fontStyle: layout === 'centered-card' ? 'italic' : 'normal',
  textTransform: layout === 'professional' ? 'uppercase' : 'none',
  letterSpacing: layout === 'professional' ? '1.5px' : 'normal',
  [theme.breakpoints.up('sm')]: {
    fontSize: '21px',
    letterSpacing: layout === 'professional' ? '2px' : 'normal'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: templateStyle?.titleFontSize || '24px'
  }
}));

const SubTitle = styled(Typography, {
  shouldForwardProp: (prop) => !['templateStyle', 'layout'].includes(prop)
})(({ theme, templateStyle, layout }) => ({
  fontFamily: `${templateStyle?.fontFamily || 'Inter'}, sans-serif`,
  fontSize: '22px',
  fontWeight: layout === 'minimalist' ? 600 : 700,
  color: layout === 'split-layout' ? templateStyle?.secondaryColor : templateStyle?.secondaryColor || '#764ba2',
  textTransform: layout === 'full-width' ? 'uppercase' : 'none',
  letterSpacing: layout === 'full-width' ? '0.5px' : 'normal',
  [theme.breakpoints.up('sm')]: {
    fontSize: '28px',
    letterSpacing: layout === 'full-width' ? '1px' : 'normal'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: templateStyle?.subtitleFontSize || '32px'
  }
}));

const ProfileSection = styled(Stack, {
  shouldForwardProp: (prop) => !['layout'].includes(prop)
})(({ theme, layout }) => {
  if (layout === 'split-layout') {
    return {
      alignItems: 'flex-start',
      marginBottom: '16px',
      gap: '10px',
      gridColumn: '1',
      [theme.breakpoints.up('sm')]: {
        marginBottom: '20px',
        gap: '12px'
      }
    };
  }

  return {
    alignItems: 'center',
    marginBottom: '16px',
    gap: '10px',
    flexDirection: layout === 'professional' ? 'row' : 'column',
    [theme.breakpoints.up('sm')]: {
      marginBottom: '20px',
      gap: '12px'
    }
  };
});

const ProfileAvatar = styled(Avatar, {
  shouldForwardProp: (prop) => !['templateStyle', 'layout'].includes(prop)
})(({ theme, templateStyle, layout }) => {
  const mobileSize = layout === 'split-layout' ? '50px' : layout === 'professional' ? '56px' : '64px';
  const tabletSize = layout === 'split-layout' ? '55px' : layout === 'professional' ? '64px' : '72px';
  const desktopSize = layout === 'split-layout' ? '60px' : layout === 'professional' ? '70px' : '80px';

  return {
    width: mobileSize,
    height: mobileSize,
    border: `2px solid ${templateStyle?.primaryColor || '#667eea'}`,
    boxShadow: layout === 'full-width' ? '0 6px 16px rgba(0,0,0,0.3)' : 'none',
    borderRadius: '8px',
    [theme.breakpoints.up('sm')]: {
      width: tabletSize,
      height: tabletSize,
      border: `3px solid ${templateStyle?.primaryColor || '#667eea'}`,
      boxShadow: layout === 'full-width' ? '0 8px 20px rgba(0,0,0,0.3)' : 'none'
    },
    [theme.breakpoints.up('md')]: {
      width: desktopSize,
      height: desktopSize
    }
  };
});

const ProfileName = styled(Typography, {
  shouldForwardProp: (prop) => !['templateStyle', 'layout'].includes(prop)
})(({ theme, templateStyle, layout }) => ({
  fontFamily: `${templateStyle?.fontFamily || 'Inter'}, sans-serif`,
  fontSize: layout === 'split-layout' ? '16px' : '17px',
  fontWeight: 600,
  color: layout === 'split-layout' ? templateStyle?.textColor : templateStyle?.primaryColor || '#667eea',
  [theme.breakpoints.up('sm')]: {
    fontSize: layout === 'split-layout' ? '18px' : '19px'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: layout === 'split-layout' ? '18px' : '20px'
  }
}));

const ProfileTitle = styled(Typography, {
  shouldForwardProp: (prop) => !['templateStyle', 'layout'].includes(prop)
})(({ theme, templateStyle, layout }) => ({
  fontFamily: `${templateStyle?.fontFamily || 'Inter'}, sans-serif`,
  fontSize: '12px',
  fontWeight: 400,
  color: layout === 'full-width' ? 'rgba(255,255,255,0.8)' : '#666666',
  fontStyle: layout === 'centered-card' ? 'italic' : 'normal',
  [theme.breakpoints.up('sm')]: {
    fontSize: '13px'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '14px'
  }
}));

const TestimonialText = styled(Typography, {
  shouldForwardProp: (prop) => !['templateStyle', 'layout'].includes(prop)
})(({ theme, templateStyle, layout }) => {
  const baseStyles = {
    fontFamily: `${templateStyle?.fontFamily || 'Inter'}, sans-serif`,
    fontSize: '13px',
    fontWeight: 400,
    lineHeight: layout === 'professional' ? '1.7' : '1.5',
    marginBottom: '16px',
    fontStyle: layout === 'centered-card' ? 'italic' : 'normal',
    [theme.breakpoints.up('sm')]: {
      fontSize: templateStyle?.textFontSize || '14px',
      lineHeight: layout === 'professional' ? '1.8' : '1.6',
      marginBottom: '20px'
    }
  };

  if (layout === 'split-layout') {
    return {
      ...baseStyles,
      gridColumn: '2',
      color: templateStyle?.textColor || '#334155',
      fontSize: '13px',
      lineHeight: '1.6',
      [theme.breakpoints.up('sm')]: {
        fontSize: '14px',
        lineHeight: '1.7'
      },
      [theme.breakpoints.up('md')]: {
        fontSize: '15px'
      }
    };
  }

  if (layout === 'full-width') {
    return {
      ...baseStyles,
      color: templateStyle?.textColor || '#ffffff',
      fontSize: '14px',
      textShadow: '0 2px 4px rgba(0,0,0,0.3)',
      fontWeight: 500,
      [theme.breakpoints.up('sm')]: {
        fontSize: '15px'
      },
      [theme.breakpoints.up('md')]: {
        fontSize: '16px'
      }
    };
  }

  if (layout === 'professional') {
    return {
      ...baseStyles,
      color: templateStyle?.textColor || '#e2e8f0',
      borderLeft: `3px solid ${templateStyle?.primaryColor}`,
      paddingLeft: '12px',
      fontStyle: 'normal',
      [theme.breakpoints.up('sm')]: {
        borderLeft: `4px solid ${templateStyle?.primaryColor}`,
        paddingLeft: '16px'
      }
    };
  }

  return {
    ...baseStyles,
    color: '#333333'
  };
});

const StyledRating = styled(Rating, {
  shouldForwardProp: (prop) => !['templateStyle', 'layout'].includes(prop)
})(({ theme, templateStyle, layout }) => ({
  marginBottom: '16px',
  gridColumn: layout === 'split-layout' ? '2' : 'auto',
  '& .MuiRating-iconFilled': {
    color: templateStyle?.primaryColor || '#667eea',
    filter: layout === 'full-width' ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' : 'none'
  },
  '& .MuiRating-icon': {
    fontSize: layout === 'professional' ? '1.2rem' : '1rem'
  },
  [theme.breakpoints.up('sm')]: {
    marginBottom: '20px',
    '& .MuiRating-icon': {
      fontSize: layout === 'professional' ? '1.35rem' : '1.1rem'
    }
  },
  [theme.breakpoints.up('md')]: {
    '& .MuiRating-icon': {
      fontSize: layout === 'professional' ? '1.5rem' : '1.2rem'
    }
  }
}));

// Geometric Modern Components
const GeometricPattern = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '-50px',
  right: '-50px',
  width: '200px',
  height: '200px',
  opacity: 0.1,
  zIndex: 1
}));

const Triangle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'delay'
})(({ theme, delay = 0 }) => ({
  position: 'absolute',
  width: 0,
  height: 0,
  borderLeft: '25px solid transparent',
  borderRight: '25px solid transparent',
  borderBottom: '43px solid #ff6b6b',
  animation: `${float} 6s ease-in-out infinite`,
  animationDelay: `${delay}s`,
  '&:nth-of-type(2)': {
    top: '25px',
    left: '40px',
    borderBottomColor: '#4ecdc4',
  },
  '&:nth-of-type(3)': {
    top: '50px',
    left: '10px',
    borderBottomColor: '#45b7d1',
  }
}));

const GeometricStar = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'delay'
})(({ theme, delay = 0 }) => ({
  width: '24px',
  height: '24px',
  background: 'linear-gradient(135deg, #ffd700, #ffed4e)',
  clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
  animation: `${sparkle} 2s ease-in-out infinite`,
  animationDelay: `${delay}s`
}));

const GeometricAvatar = styled(Box, {
  shouldForwardProp: (prop) => !['templateStyle'].includes(prop)
})(({ theme, templateStyle }) => ({
  width: '70px',
  height: '70px',
  background: templateStyle?.avatarGradient || 'linear-gradient(135deg, #ff6b6b, #4ecdc4, #45b7d1)',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '24px',
  fontWeight: 700,
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '80%',
    height: '80%',
    background: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '8px',
    transform: 'translate(-50%, -50%)'
  }
}));

const VerificationBadge = styled(Box, {
  shouldForwardProp: (prop) => !['templateStyle'].includes(prop)
})(({ theme, templateStyle }) => ({
  marginLeft: 'auto',
  background: templateStyle?.verificationGradient || 'linear-gradient(135deg, #4ecdc4, #45b7d1)',
  color: 'white',
  padding: '10px 16px',
  borderRadius: '8px',
  fontSize: '13px',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  backdropFilter: 'blur(10px)',
  '&::before': {
    content: '"✓"',
    background: 'white',
    color: '#4ecdc4',
    width: '18px',
    height: '18px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 'bold'
  }
}));

const GlowEffect = styled(Box, {
  shouldForwardProp: (prop) => !['templateStyle'].includes(prop)
})(({ theme, templateStyle }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  height: '2px',
  background: templateStyle?.glowGradient || 'linear-gradient(90deg, #ff6b6b, #4ecdc4, #45b7d1, #ffd700)',
  animation: `${glow} 3s ease-in-out infinite alternate`
}));

// Creative Geometric Components
const CreativeGeometricBg = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '-2rem',
  left: '-2rem',
  width: 'calc(100% + 4rem)',
  height: 'calc(100% + 4rem)',
  zIndex: -1
}));

const GeometricShape = styled(Box, {
  shouldForwardProp: (prop) => !['templateStyle', 'shapeType'].includes(prop)
})(({ theme, templateStyle, shapeType }) => {
  const baseStyles = {
    position: 'absolute',
    opacity: 0.6
  };

  if (shapeType === 'shape1') {
    return {
      ...baseStyles,
      top: 0,
      right: 0,
      width: '120px',
      height: '120px',
      background: templateStyle?.shape1Gradient || 'linear-gradient(135deg, #db2777 0%, #be185d 100%)',
      clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
      animation: `${rotate} 20s linear infinite`
    };
  }

  if (shapeType === 'shape2') {
    return {
      ...baseStyles,
      bottom: 0,
      left: 0,
      width: '100px',
      height: '100px',
      background: templateStyle?.shape2Gradient || 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
      borderRadius: '8px',
      animation: `${pulse} 4s ease-in-out infinite`
    };
  }

  if (shapeType === 'shape3') {
    return {
      ...baseStyles,
      top: '50%',
      right: '-1rem',
      width: '60px',
      height: '60px',
      background: templateStyle?.shape3Gradient || 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      transform: 'rotate(45deg)',
      animation: `${floatShape} 6s ease-in-out infinite`
    };
  }

  return baseStyles;
});

const CreativeQuoteMark = styled(Box, {
  shouldForwardProp: (prop) => !['templateStyle'].includes(prop)
})(({ theme, templateStyle }) => ({
  width: '60px',
  height: '60px',
  background: templateStyle?.quoteGradient || 'linear-gradient(135deg, #db2777 0%, #be185d 100%)',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
  fontSize: '2rem',
  fontFamily: 'Georgia, serif',
  fontWeight: 'bold',
  position: 'relative',
  boxShadow: '0 8px 25px rgba(219, 39, 119, 0.3)',
  '&::before': {
    content: '"\\201C"'
  }
}));

const CreativeRatingBadge = styled(Box, {
  shouldForwardProp: (prop) => !['templateStyle'].includes(prop)
})(({ theme, templateStyle }) => ({
  background: templateStyle?.ratingBackground || 'rgba(59, 130, 246, 0.15)',
  border: templateStyle?.ratingBorder || '2px solid rgba(59, 130, 246, 0.3)',
  borderRadius: '8px',
  padding: '0.75rem 1.25rem',
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem'
}));

const CreativeStar = styled(Box)(({ theme }) => ({
  width: '18px',
  height: '18px',
  '&::before': {
    content: '"★"',
    color: '#fbbf24',
    fontSize: '18px'
  }
}));

const CreativeAvatar = styled(Box, {
  shouldForwardProp: (prop) => !['templateStyle'].includes(prop)
})(({ theme, templateStyle }) => ({
  width: '70px',
  height: '70px',
  borderRadius: '16px',
  background: templateStyle?.avatarGradient || 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
  fontSize: '1.5rem',
  fontWeight: 700,
  border: templateStyle?.avatarBorder || '3px solid rgba(16, 185, 129, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'rotate(-5deg) scale(1.05)'
  }
}));

const VerifiedDot = styled(Box, {
  shouldForwardProp: (prop) => !['templateStyle'].includes(prop)
})(({ theme, templateStyle }) => ({
  position: 'absolute',
  top: '-5px',
  right: '-5px',
  width: '24px',
  height: '24px',
  background: templateStyle?.verifiedColor || '#10b981',
  borderRadius: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
  fontSize: '12px',
  fontWeight: 'bold',
  border: `3px solid ${templateStyle?.background || '#111827'}`,
  '&::before': {
    content: '"✓"'
  }
}));

const ReviewDate = styled(Box, {
  shouldForwardProp: (prop) => !['templateStyle'].includes(prop)
})(({ theme, templateStyle }) => ({
  color: templateStyle?.dateColor || '#6b7280',
  fontSize: '0.75rem',
  background: templateStyle?.dateBackground || 'rgba(75, 85, 99, 0.2)',
  padding: '0.375rem 0.75rem',
  borderRadius: '6px',
  border: templateStyle?.dateBorder || '1px solid rgba(75, 85, 99, 0.3)',
  marginLeft: 'auto'
}));

// Professional Blue Components
const BlueHeader = styled(Typography, {
  shouldForwardProp: (prop) => !['templateStyle'].includes(prop)
})(({ theme, templateStyle }) => ({
  fontFamily: templateStyle?.fontFamily || 'Inter',
  fontSize: templateStyle?.titleFontSize || '64px',
  fontWeight: templateStyle?.headerFontWeight || 900,
  color: templateStyle?.headerColor || '#ffffff',
  textAlign: 'center',
  letterSpacing: templateStyle?.headerLetterSpacing || '0.05em',
  marginBottom: '2rem',
  textTransform: 'uppercase',
  position: 'relative',
  zIndex: 2
}));

const BlueAvatar = styled(Avatar, {
  shouldForwardProp: (prop) => !['templateStyle'].includes(prop)
})(({ theme, templateStyle }) => ({
  width: templateStyle?.avatarSize || '140px',
  height: templateStyle?.avatarSize || '140px',
  border: templateStyle?.avatarBorder || '6px solid #2d4373',
  position: 'absolute',
  top: '-70px',
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 10,
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
  borderRadius: '8px'
}));

const BlueQuoteCard = styled(Box, {
  shouldForwardProp: (prop) => !['templateStyle'].includes(prop)
})(({ theme, templateStyle }) => ({
  background: templateStyle?.cardBackground || '#f3f4f6',
  borderRadius: '0px',
  border: templateStyle?.cardBorder || '4px solid #2d4373',
  padding: '5rem 3rem 3rem 3rem',
  position: 'relative',
  marginTop: '70px',
  marginBottom: '2rem',
  minHeight: '280px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  zIndex: 1
}));

const BlueQuoteMark = styled(Typography, {
  shouldForwardProp: (prop) => !['templateStyle', 'position'].includes(prop)
})(({ theme, templateStyle, position }) => ({
  fontSize: templateStyle?.quoteFontSize || '64px',
  color: templateStyle?.quoteColor || '#2d4373',
  fontFamily: 'Georgia, serif',
  fontWeight: 'bold',
  position: 'absolute',
  top: position === 'left' ? '80px' : '80px',
  left: position === 'left' ? '40px' : 'auto',
  right: position === 'right' ? '40px' : 'auto',
  lineHeight: 1,
  opacity: 0.8,
  zIndex: 1
}));

const BlueStar = styled(Box, {
  shouldForwardProp: (prop) => !['templateStyle'].includes(prop)
})(({ theme, templateStyle }) => ({
  fontSize: '32px',
  color: templateStyle?.starColor || '#2d4373',
  lineHeight: 1,
  '&::before': {
    content: '"★"'
  }
}));

const BlueNameBadge = styled(Box, {
  shouldForwardProp: (prop) => !['templateStyle'].includes(prop)
})(({ theme, templateStyle }) => ({
  background: templateStyle?.nameBadgeBackground || '#2d4373',
  color: templateStyle?.nameBadgeColor || '#ffffff',
  padding: templateStyle?.nameBadgePadding || '1.5rem 4rem',
  borderRadius: templateStyle?.nameBadgeRadius || '50px',
  textAlign: 'center',
  fontSize: templateStyle?.nameBadgeFontSize || '28px',
  fontWeight: templateStyle?.nameBadgeFontWeight || 700,
  fontFamily: templateStyle?.fontFamily || 'Inter',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  boxShadow: '0 8px 25px rgba(45, 67, 115, 0.4)',
  position: 'relative',
  zIndex: 2
}));

// Orange Accent Components
const OrangeQuoteMark = styled(Box, {
  shouldForwardProp: (prop) => !['templateStyle', 'position'].includes(prop)
})(({ theme, templateStyle, position }) => ({
  fontSize: templateStyle?.quoteFontSize || '120px',
  color: templateStyle?.quoteColor || '#ff8c42',
  fontFamily: 'Georgia, serif',
  fontWeight: 'bold',
  position: 'absolute',
  opacity: templateStyle?.quoteOpacity || 0.15,
  lineHeight: 1,
  zIndex: 0,
  ...(position === 'top-left' && {
    top: '-20px',
    left: '20px'
  }),
  ...(position === 'bottom-right' && {
    bottom: '40px',
    right: '40px'
  })
}));

const OrangeNameBadge = styled(Box, {
  shouldForwardProp: (prop) => !['templateStyle'].includes(prop)
})(({ theme, templateStyle }) => ({
  background: templateStyle?.nameBadgeBackground || 'linear-gradient(135deg, #ff8c42 0%, #ff6b35 100%)',
  color: templateStyle?.nameBadgeColor || '#ffffff',
  padding: templateStyle?.nameBadgePadding || '1rem 2rem',
  borderRadius: templateStyle?.nameBadgeRadius || '50px',
  display: 'flex',
  alignItems: 'center',
  gap: '1rem',
  position: 'absolute',
  top: '-30px',
  left: '50%',
  transform: 'translateX(-50%)',
  boxShadow: '0 10px 30px rgba(255, 140, 66, 0.4)',
  zIndex: 20
}));

const OrangeAvatar = styled(Avatar, {
  shouldForwardProp: (prop) => !['templateStyle'].includes(prop)
})(({ theme, templateStyle }) => ({
  width: templateStyle?.avatarSize || '70px',
  height: templateStyle?.avatarSize || '70px',
  border: templateStyle?.avatarBorder || '4px solid #ffffff',
  borderRadius: '8px'
}));

const OrangeStar = styled(Box, {
  shouldForwardProp: (prop) => !['templateStyle'].includes(prop)
})(({ theme, templateStyle }) => ({
  fontSize: templateStyle?.starSize || '32px',
  color: templateStyle?.starColor || '#ff8c42',
  lineHeight: 1,
  '&::before': {
    content: '"★"'
  }
}));

const DecorativeDots = styled(Box, {
  shouldForwardProp: (prop) => !['templateStyle', 'position'].includes(prop)
})(({ theme, templateStyle, position }) => ({
  position: 'absolute',
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 8px)',
  gap: '8px',
  opacity: templateStyle?.decorativeDotsOpacity || 0.3,
  zIndex: 1,
  ...(position === 'top-right' && {
    top: '20px',
    right: '20px'
  }),
  ...(position === 'bottom-left' && {
    bottom: '20px',
    left: '20px'
  }),
  '& > div': {
    width: '8px',
    height: '8px',
    borderRadius: '2px',
    background: templateStyle?.decorativeDots || '#ffffff'
  }
}));

const OrangeCard = styled(Box, {
  shouldForwardProp: (prop) => !['templateStyle'].includes(prop)
})(({ theme, templateStyle }) => ({
  background: templateStyle?.cardBackground || '#ffffff',
  borderRadius: '0px',
  padding: '3rem',
  position: 'relative',
  maxWidth: '600px',
  width: '100%',
  boxShadow: templateStyle?.cardShadow || '0 25px 50px rgba(0, 0, 0, 0.2)',
  zIndex: 10
}));

const QuoteIcon = styled(Box, {
  shouldForwardProp: (prop) => !['templateStyle', 'layout'].includes(prop)
})(({ theme, templateStyle, layout }) => {
  if (layout === 'professional') {
    return {
      position: 'absolute',
      top: '16px',
      right: '16px',
      fontSize: '48px',
      color: templateStyle?.primaryColor,
      opacity: 0.3,
      fontFamily: 'serif',
      '&::before': {
        content: '"\\201C"'
      }
    };
  }

  if (layout === 'geometric-modern') {
    return {
      position: 'absolute',
      top: '-10px',
      left: '-10px',
      fontSize: '120px',
      color: 'rgba(255, 107, 107, 0.2)',
      fontFamily: 'serif',
      lineHeight: 1,
      pointerEvents: 'none',
      zIndex: 1,
      '&::before': {
        content: '"\\201C"'
      }
    };
  }

  return { display: 'none' };
});

const EnhancedPostPreview = ({ generatedPost, selectedTemplate }) => {
  const templateStyle = selectedTemplate?.style;
  const layout = selectedTemplate?.layout || 'centered-card';

  // Render orange accent layout
  if (layout === 'orange-accent') {
    return (
      <PreviewContainer
        templateStyle={templateStyle}
        layout={layout}
        className="post-preview-container"
      >
        <DecorativeDots templateStyle={templateStyle} position="top-right">
          <div /><div /><div />
          <div /><div /><div />
          <div /><div /><div />
        </DecorativeDots>

        <DecorativeDots templateStyle={templateStyle} position="bottom-left">
          <div /><div /><div />
          <div /><div /><div />
          <div /><div /><div />
        </DecorativeDots>

        <OrangeCard templateStyle={templateStyle}>
          <OrangeNameBadge templateStyle={templateStyle}>
            <OrangeAvatar
              templateStyle={templateStyle}
              src={generatedPost.userProfile.avatar}
              alt={generatedPost.userProfile.name}
              imgProps={defaultAvatarImgProps}
            />
            <Box>
              <Typography
                sx={{
                  fontSize: templateStyle?.nameFontSize || '24px',
                  fontWeight: templateStyle?.nameFontWeight || 700,
                  fontFamily: templateStyle?.fontFamily,
                  lineHeight: 1.2
                }}
              >
                {generatedPost.userProfile.name}
              </Typography>
              <Typography
                sx={{
                  fontSize: templateStyle?.subtitleFontSize || '16px',
                  fontWeight: 400,
                  fontFamily: templateStyle?.fontFamily,
                  color: templateStyle?.subtitleColor || 'rgba(255, 255, 255, 0.8)',
                  lineHeight: 1.2
                }}
              >
                {generatedPost.userProfile.title}
              </Typography>
            </Box>
          </OrangeNameBadge>

          <OrangeQuoteMark templateStyle={templateStyle} position="top-left">
            "
          </OrangeQuoteMark>
          <OrangeQuoteMark templateStyle={templateStyle} position="bottom-right">
            "
          </OrangeQuoteMark>

          <Typography
            sx={{
              fontSize: templateStyle?.textFontSize || '22px',
              lineHeight: 1.6,
              color: templateStyle?.textColor || '#374151',
              fontWeight: 400,
              textAlign: 'left',
              marginBottom: '2rem',
              paddingTop: '2rem',
              fontFamily: templateStyle?.fontFamily,
              position: 'relative',
              zIndex: 2
            }}
          >
            {generatedPost.testimonialText}
          </Typography>

          <Box
            sx={{
              borderTop: '2px solid #e5e7eb',
              paddingTop: '1.5rem',
              position: 'relative',
              zIndex: 2
            }}
          >
            <Stack direction="row" justifyContent="flex-start" gap="6px">
              {[...Array(Math.round(generatedPost.rating || 5))].map((_, index) => (
                <OrangeStar key={index} templateStyle={templateStyle} />
              ))}
            </Stack>
          </Box>
        </OrangeCard>
      </PreviewContainer>
    );
  }

  // Render PREMIUM PROFESSIONAL layout
  if (layout === 'professional-blue') {
    return (
      <PreviewContainer
        templateStyle={templateStyle}
        layout={layout}
        className="post-preview-container"
        sx={{
          background: templateStyle?.background,
          boxShadow: templateStyle?.shadow,
          borderRadius: templateStyle?.borderRadius,
          padding: '56px 48px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '0px',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Decorative Background Quote */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: templateStyle?.quoteFontSize,
            fontWeight: 900,
            color: templateStyle?.quoteColor,
            opacity: templateStyle?.quoteOpacity,
            lineHeight: 1,
            fontFamily: 'Georgia, serif',
            zIndex: 0,
            pointerEvents: 'none'
          }}
        >
          ❝
        </Box>

        {/* Header Text */}
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: templateStyle?.headerFontWeight,
            color: templateStyle?.headerColor,
            letterSpacing: templateStyle?.headerLetterSpacing,
            textTransform: 'uppercase',
            marginBottom: '40px',
            zIndex: 2,
            textShadow: templateStyle?.glowEffect
          }}
        >
          {templateStyle?.headerText || 'CLIENT TESTIMONIAL'}
        </Typography>

        {/* Customer Avatar with Glow */}
        <Box sx={{ position: 'relative', marginBottom: '32px', zIndex: 2 }}>
          <Avatar
            src={generatedPost.userProfile.avatar}
            alt={generatedPost.userProfile.name}
            sx={{
              width: templateStyle?.avatarSize,
              height: templateStyle?.avatarSize,
              border: templateStyle?.avatarBorder,
              boxShadow: templateStyle?.avatarShadow,
              filter: 'brightness(1.05)'
            }}
            imgProps={defaultAvatarImgProps}
          />
          {/* Glow ring behind avatar */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 'calc(100% + 20px)',
              height: 'calc(100% + 20px)',
              borderRadius: '50%',
              background: `radial-gradient(circle, ${templateStyle?.secondaryColor}20 0%, transparent 70%)`,
              zIndex: -1
            }}
          />
        </Box>

        {/* Star Rating with glow */}
        <Stack direction="row" gap="8px" sx={{ marginBottom: '24px', zIndex: 2 }}>
          {[...Array(Math.round(generatedPost.rating || 5))].map((_, index) => (
            <Box
              key={index}
              sx={{
                fontSize: templateStyle?.starSize,
                color: templateStyle?.starColor,
                filter: `drop-shadow(0 0 8px ${templateStyle?.starColor}40)`
              }}
            >
              ★
            </Box>
          ))}
        </Stack>

        {/* Testimonial Text */}
        <Typography
          sx={{
            fontSize: templateStyle?.textFontSize,
            lineHeight: templateStyle?.lineHeight,
            color: templateStyle?.textColor,
            fontWeight: 400,
            textAlign: 'center',
            fontFamily: templateStyle?.fontFamily,
            marginBottom: '36px',
            maxWidth: '85%',
            zIndex: 2,
            position: 'relative'
          }}
        >
          "{generatedPost.testimonialText}"
        </Typography>

        {/* Decorative divider line */}
        <Box
          sx={{
            width: '120px',
            height: templateStyle?.accentLineHeight,
            background: `linear-gradient(90deg, transparent 0%, ${templateStyle?.accentLine} 50%, transparent 100%)`,
            marginBottom: '32px',
            boxShadow: templateStyle?.glowEffect
          }}
        />

        {/* Customer Name */}
        <Typography
          sx={{
            fontSize: templateStyle?.nameFontSize,
            fontWeight: templateStyle?.nameFontWeight,
            color: templateStyle?.nameColor,
            fontFamily: templateStyle?.fontFamily,
            textAlign: 'center',
            marginBottom: '8px',
            zIndex: 2,
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}
        >
          {generatedPost.userProfile.name}
        </Typography>

        {/* Customer Title with accent color */}
        <Typography
          sx={{
            fontSize: templateStyle?.titleFontSize2,
            fontWeight: templateStyle?.titleFontWeight,
            color: templateStyle?.titleColor,
            fontFamily: templateStyle?.fontFamily,
            textAlign: 'center',
            zIndex: 2,
            letterSpacing: '0.03em'
          }}
        >
          {generatedPost.userProfile.title}
        </Typography>

        {/* Bottom decorative elements */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: `linear-gradient(90deg, transparent 0%, ${templateStyle?.accentLine} 50%, transparent 100%)`,
            opacity: 0.5
          }}
        />
      </PreviewContainer>
    );
  }

  // Render geometric modern layout
  if (layout === 'geometric-modern') {
    return (
      <PreviewContainer
        templateStyle={templateStyle}
        layout={layout}
        className="post-preview-container"
      >
        <ContentCard templateStyle={templateStyle} layout={layout}>
          <GeometricPattern>
            <Triangle delay={0} />
            <Triangle delay={-2} />
            <Triangle delay={-4} />
          </GeometricPattern>

          <QuoteIcon templateStyle={templateStyle} layout={layout} />

          <Stack direction="row" alignItems="center" gap="12px" mb="24px" position="relative" zIndex={2}>
            <Stack direction="row" gap="3px">
              {[...Array(Math.round(generatedPost.rating || 5))].map((_, index) => (
                <GeometricStar key={index} delay={index * 0.2} />
              ))}
            </Stack>
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                background: templateStyle?.ratingTextGradient,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              {generatedPost.rating}.0 Perfect
            </Typography>
          </Stack>

          <Typography
            sx={{
              fontSize: templateStyle?.textFontSize || '18px',
              lineHeight: 1.7,
              marginBottom: '32px',
              position: 'relative',
              zIndex: 2,
              color: templateStyle?.textColor,
              fontWeight: 300,
              fontFamily: templateStyle?.fontFamily
            }}
          >
            "{generatedPost.testimonialText}"
          </Typography>

          <Stack
            direction="row"
            alignItems="center"
            gap="20px"
            position="relative"
            zIndex={2}
            paddingTop="24px"
            borderTop="1px solid rgba(255, 255, 255, 0.1)"
          >
            <GeometricAvatar templateStyle={templateStyle}>
              {generatedPost.userProfile.name.split(' ').map(n => n[0]).join('').toUpperCase()}
            </GeometricAvatar>
            <Box>
              <Typography
                sx={{
                  fontSize: '20px',
                  fontWeight: 600,
                  marginBottom: '6px',
                  background: templateStyle?.nameGradient,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  fontFamily: templateStyle?.fontFamily
                }}
              >
                {generatedPost.userProfile.name}
              </Typography>
              <Typography
                sx={{
                  fontSize: '14px',
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontWeight: 400,
                  fontFamily: templateStyle?.fontFamily
                }}
              >
                {generatedPost.userProfile.title} • Electronics
              </Typography>
            </Box>
            <VerificationBadge templateStyle={templateStyle}>
              Verified Review
            </VerificationBadge>
          </Stack>

          <GlowEffect templateStyle={templateStyle} />
        </ContentCard>
      </PreviewContainer>
    );
  }

  // Render creative geometric layout
  if (layout === 'creative-geometric') {
    return (
      <PreviewContainer
        templateStyle={templateStyle}
        layout={layout}
        className="post-preview-container"
      >
        <Box sx={{ maxWidth: '550px', width: '100%', position: 'relative' }}>
          <CreativeGeometricBg>
            <GeometricShape templateStyle={templateStyle} shapeType="shape1" />
            <GeometricShape templateStyle={templateStyle} shapeType="shape2" />
            <GeometricShape templateStyle={templateStyle} shapeType="shape3" />
          </CreativeGeometricBg>

          <ContentCard templateStyle={templateStyle} layout={layout}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              marginBottom="2rem"
            >
              <CreativeQuoteMark templateStyle={templateStyle} />
              <CreativeRatingBadge templateStyle={templateStyle}>
                <Stack direction="row" gap="4px">
                  {[...Array(Math.round(generatedPost.rating || 5))].map((_, index) => (
                    <CreativeStar key={index} />
                  ))}
                </Stack>
                <Typography
                  sx={{
                    color: templateStyle?.ratingTextColor || '#60a5fa',
                    fontWeight: 600,
                    fontSize: '0.875rem'
                  }}
                >
                  Excellent
                </Typography>
              </CreativeRatingBadge>
            </Stack>

            <Box marginBottom="2.5rem">
              <Typography
                sx={{
                  fontSize: templateStyle?.textFontSize || '1.25rem',
                  lineHeight: 1.6,
                  color: templateStyle?.textColor || '#e5e7eb',
                  fontWeight: 400,
                  position: 'relative',
                  paddingLeft: '1.5rem',
                  fontFamily: templateStyle?.fontFamily,
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: '4px',
                    height: '100%',
                    background: templateStyle?.textBorderGradient || 'linear-gradient(180deg, #db2777 0%, #3b82f6 50%, #10b981 100%)',
                    borderRadius: '2px'
                  }
                }}
              >
                "{generatedPost.testimonialText}"
              </Typography>
            </Box>

            <Stack
              direction="row"
              alignItems="center"
              gap="1.5rem"
              paddingTop="1.5rem"
              borderTop={`1px solid ${templateStyle?.borderTopColor || 'rgba(75, 85, 99, 0.3)'}`}
              position="relative"
            >
              <Box position="relative">
                <CreativeAvatar templateStyle={templateStyle}>
                  {generatedPost.userProfile.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                </CreativeAvatar>
                <VerifiedDot templateStyle={templateStyle} />
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: '1.375rem',
                    fontWeight: 700,
                    marginBottom: '0.25rem',
                    background: templateStyle?.nameGradient || 'linear-gradient(135deg, #f9fafb 0%, #d1d5db 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    fontFamily: templateStyle?.fontFamily
                  }}
                >
                  {generatedPost.userProfile.name}
                </Typography>
                <Typography
                  sx={{
                    color: templateStyle?.detailsColor || '#9ca3af',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    fontFamily: templateStyle?.fontFamily
                  }}
                >
                  {generatedPost.userProfile.title}
                </Typography>
              </Box>

              <ReviewDate templateStyle={templateStyle}>
                Sept 2025
              </ReviewDate>
            </Stack>
          </ContentCard>
        </Box>
      </PreviewContainer>
    );
  }

  // Render client-feedback layout
  if (layout === 'client-feedback') {
    return (
      <ClientFeedbackOuter templateStyle={templateStyle} className="post-preview-container">
        <ClientFeedbackBlob variant="warm" templateStyle={templateStyle} />
        <ClientFeedbackBlob variant="cool" templateStyle={templateStyle} />

        <ClientFeedbackContent>
          <ClientFeedbackTitle templateStyle={templateStyle}>
            {(templateStyle?.headerText || 'Clients')}{' '}
            <ClientFeedbackHighlight templateStyle={templateStyle}>
              {templateStyle?.headerAccent || 'Feedback'}
            </ClientFeedbackHighlight>
          </ClientFeedbackTitle>

          <ClientFeedbackCardWrapper>
            <ClientFeedbackAvatarWrapper templateStyle={templateStyle}>
              <ClientFeedbackAvatar templateStyle={templateStyle}>
                {generatedPost.userProfile?.avatar ? (
                  <img
                    src={generatedPost.userProfile.avatar}
                    alt={generatedPost.userProfile?.name || 'Client avatar'}
                    crossOrigin="anonymous"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  (generatedPost.userProfile?.name || 'Client')
                    .split(' ')
                    .map((word) => word[0])
                    .join('')
                    .toUpperCase()
                )}
              </ClientFeedbackAvatar>
            </ClientFeedbackAvatarWrapper>

            <ClientFeedbackCard templateStyle={templateStyle}>
              <ClientFeedbackPattern templateStyle={templateStyle} />

              <ClientFeedbackCardContent>
                <Typography
                  sx={{
                    fontSize: templateStyle?.nameFontSize || '32px',
                    fontWeight: templateStyle?.nameFontWeight || 600,
                    color: templateStyle?.nameColor || '#ffffff',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}
                >
                  {generatedPost.userProfile?.name || 'Client Name'}
                </Typography>

                <ClientFeedbackVerified templateStyle={templateStyle}>
                  {generatedPost.userProfile?.title || 'Verified Customer'}
                </ClientFeedbackVerified>

                <ClientFeedbackText templateStyle={templateStyle}>
                  {generatedPost.testimonialText || 'Help me to purchase 2BHK in Mohali'}
                </ClientFeedbackText>

                <ClientFeedbackStars>
                  {[...Array(5)].map((_, index) => (
                    <ClientFeedbackStar
                      key={`client-feedback-star-${index}`}
                      filled={index < Math.round(generatedPost.rating || 5)}
                      templateStyle={templateStyle}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                        <polygon points="12 2 15.09 10.26 24 10.26 17.55 15.74 19.64 24 12 18.52 4.36 24 6.45 15.74 0 10.26 8.91 10.26" />
                      </svg>
                    </ClientFeedbackStar>
                  ))}
                </ClientFeedbackStars>
              </ClientFeedbackCardContent>
            </ClientFeedbackCard>
          </ClientFeedbackCardWrapper>
        </ClientFeedbackContent>
      </ClientFeedbackOuter>
    );
  }

  // Render FRESH PROFESSIONAL layout
  if (layout === 'fresh-professional') {
    return (
      <PreviewContainer
        templateStyle={templateStyle}
        layout={layout}
        className="post-preview-container"
        sx={{
          background: templateStyle?.background || '#f8fafc',
          padding: '60px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Background Accents */}
        <Box
          sx={{
            position: 'absolute',
            top: '-10%',
            right: '-5%',
            width: '300px',
            height: '300px',
            background: `radial-gradient(circle, ${templateStyle?.primaryColor}15 0%, transparent 70%)`,
            borderRadius: '50%',
            zIndex: 0
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '-10%',
            left: '-5%',
            width: '250px',
            height: '250px',
            background: `radial-gradient(circle, ${templateStyle?.secondaryColor}15 0%, transparent 70%)`,
            borderRadius: '50%',
            zIndex: 0
          }}
        />

        <ContentCard
          templateStyle={templateStyle}
          layout={layout}
          sx={{
            background: '#ffffff',
            borderRadius: '24px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.08)',
            padding: { xs: '32px', md: '48px' },
            maxWidth: '640px',
            width: '100%',
            position: 'relative',
            zIndex: 1,
            border: '1px solid rgba(241, 245, 249, 1)'
          }}
        >
          {/* Verified Badge */}
          <Box
            sx={{
              position: 'absolute',
              top: '32px',
              right: '32px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: `${templateStyle?.primaryColor}10`,
              padding: '6px 12px',
              borderRadius: '100px',
              border: `1px solid ${templateStyle?.primaryColor}20`
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: templateStyle?.primaryColor || '#0ea5e9',
                boxShadow: `0 0 8px ${templateStyle?.primaryColor}60`
              }}
            />
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 600,
                color: templateStyle?.primaryColor || '#0ea5e9',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}
            >
              Verified Review
            </Typography>
          </Box>

          {/* Quote Icon */}
          <Box
            sx={{
              fontSize: '80px',
              color: templateStyle?.primaryColor || '#0ea5e9',
              fontFamily: 'serif',
              lineHeight: 0.4,
              marginBottom: '16px',
              opacity: 0.2,
              marginLeft: '-4px'
            }}
          >
            “
          </Box>

          {/* Testimonial Text */}
          <Typography
            sx={{
              fontSize: templateStyle?.textFontSize || '20px',
              lineHeight: 1.6,
              color: templateStyle?.textColor || '#334155',
              fontFamily: templateStyle?.fontFamily || 'Inter, sans-serif',
              fontWeight: 400,
              marginBottom: '32px',
              position: 'relative',
              zIndex: 2
            }}
          >
            {generatedPost.testimonialText}
          </Typography>

          {/* Rating */}
          <Stack direction="row" spacing={0.5} mb={4}>
            {[...Array(5)].map((_, index) => (
              <Box
                key={index}
                sx={{
                  color: index < Math.round(generatedPost.rating || 5)
                    ? (templateStyle?.starColor || '#fbbf24')
                    : '#e2e8f0',
                  fontSize: '20px'
                }}
              >
                ★
              </Box>
            ))}
          </Stack>

          <Box sx={{ height: '1px', background: '#f1f5f9', width: '100%', marginBottom: '32px' }} />

          {/* User Profile */}
          <Stack direction="row" alignItems="center" spacing={2.5}>
            <Avatar
              src={generatedPost.userProfile.avatar}
              alt={generatedPost.userProfile.name}
              sx={{
                width: 64,
                height: 64,
                border: `3px solid #ffffff`,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
              }}
              imgProps={defaultAvatarImgProps}
            />
            <Box>
              <Typography
                sx={{
                  fontSize: '18px',
                  fontWeight: 700,
                  color: '#0f172a',
                  fontFamily: templateStyle?.fontFamily || 'Inter, sans-serif',
                  lineHeight: 1.2,
                  marginBottom: '4px'
                }}
              >
                {generatedPost.userProfile.name}
              </Typography>
              <Typography
                sx={{
                  fontSize: '14px',
                  color: '#64748b',
                  fontFamily: templateStyle?.fontFamily || 'Inter, sans-serif',
                  fontWeight: 500
                }}
              >
                {generatedPost.userProfile.title}
              </Typography>
            </Box>

            {/* Company Logo Placeholder or Brand Element */}
            <Box sx={{ flexGrow: 1 }} />
            <Box
              sx={{
                opacity: 0.8,
                filter: 'grayscale(100%)',
                transition: 'all 0.3s',
                '&:hover': { filter: 'none', opacity: 1 }
              }}
            >
              {/* Optional: Add a small logo or brand mark here if available in props */}
            </Box>
          </Stack>
        </ContentCard>
      </PreviewContainer>
    );
  }

  // Render client-testimonials layout
  if (layout === 'client-testimonials') {
    return (
      <PreviewContainer
        templateStyle={templateStyle}
        layout={layout}
        className="post-preview-container"
        sx={{
          background: templateStyle?.background,
          padding: {
            xs: '50px 15px 40px',     // Mobile - more top padding
            sm: '60px 20px 50px',     // Tablet - more top padding
            md: '70px 15px 60px'      // Desktop - more top padding
          },
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          minHeight: {
            xs: 'auto',
            sm: '500px'
          }
        }}
      >
        {/* Header: Clients TESTIMONIALS */}
        <Typography
          sx={{
            fontSize: {
              xs: '28px',        // Mobile
              sm: '34px',        // Tablet
              md: templateStyle?.headerFontSize || '38px'  // Desktop
            },
            fontWeight: templateStyle?.headerFontWeight || 300,
            marginTop: {
              xs: '10px',        // Mobile - add top margin
              sm: '15px',        // Tablet - add top margin
              md: '20px'         // Desktop - add top margin
            },
            marginBottom: {
              xs: '40px',        // Mobile
              sm: '55px',        // Tablet
              md: templateStyle?.headerMarginBottom || '70px'  // Desktop
            },
            color: templateStyle?.headerColor || '#ffffff',
            fontFamily: templateStyle?.fontFamily,
            textAlign: 'center',
            px: 2
          }}
        >
          {templateStyle?.headerText || 'Clients'}{' '}
          <Box
            component="span"
            sx={{
              color: templateStyle?.headerAccentColor || '#ffffff',
              fontWeight: templateStyle?.headerAccentWeight || 800
            }}
          >
            {templateStyle?.headerAccent || 'TESTIMONIALS'}
          </Box>
        </Typography>

        {/* Card with avatar at top */}
        <Box
          sx={{
            maxWidth: {
              xs: '100%',        // Mobile - full width
              sm: '600px',       // Tablet
              md: '700px'        // Desktop
            },
            width: '100%',
            textAlign: 'center',
            position: 'relative',
            px: {
              xs: 2,             // Mobile padding
              sm: 3,             // Tablet padding
              md: 0              // Desktop no extra padding
            }
          }}
        >
          {/* Avatar positioned at top center */}
          <Avatar
            src={generatedPost.userProfile.avatar}
            alt={generatedPost.userProfile.name}
            sx={{
              width: {
                xs: '100px',     // Mobile
                sm: '125px',     // Tablet
                md: templateStyle?.avatarSize || '150px'  // Desktop
              },
              height: {
                xs: '100px',     // Mobile
                sm: '125px',     // Tablet
                md: templateStyle?.avatarSize || '150px'  // Desktop
              },
              border: {
                xs: '4px solid #fff',  // Mobile
                sm: '5px solid #fff',  // Tablet
                md: templateStyle?.avatarBorder || '6px solid #fff'  // Desktop
              },
              position: 'absolute',
              top: {
                xs: '-50px',     // Mobile
                sm: '-62px',     // Tablet
                md: templateStyle?.avatarTop || '-75px'  // Desktop
              },
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 2
            }}
            imgProps={defaultAvatarImgProps}
          />

          {/* Dark card */}
          <Box
            sx={{
              background: templateStyle?.cardBackground || '#0a2948',
              padding: {
                xs: '60px 25px 40px',    // Mobile
                sm: '75px 35px 45px',    // Tablet
                md: templateStyle?.cardPadding || '90px 40px 50px'  // Desktop
              },
              borderRadius: {
                xs: '15px',      // Mobile
                sm: '18px',      // Tablet
                md: templateStyle?.borderRadius || '20px'  // Desktop
              },
              position: 'relative',
              color: templateStyle?.textColor || '#fff',
              border: templateStyle?.cardBorder || '1px solid rgba(255, 255, 255, 0.3)'
            }}
          >
            {/* Name */}
            <Typography
              sx={{
                fontSize: {
                  xs: '18px',    // Mobile
                  sm: '20px',    // Tablet
                  md: templateStyle?.nameFontSize || '22px'  // Desktop
                },
                marginTop: {
                  xs: '5px',     // Mobile
                  sm: '8px',     // Tablet
                  md: templateStyle?.nameMarginTop || '10px'  // Desktop
                },
                fontWeight: templateStyle?.nameFontWeight || 700,
                color: templateStyle?.nameColor || '#3ad1d6',
                fontFamily: templateStyle?.fontFamily,
                textTransform: 'uppercase',
                wordBreak: 'break-word'
              }}
            >
              {generatedPost.userProfile.name}
            </Typography>

            {/* Title/Role */}
            <Typography
              sx={{
                fontSize: {
                  xs: '12px',    // Mobile
                  sm: '13px',    // Tablet
                  md: templateStyle?.titleFontSize || '14px'  // Desktop
                },
                color: templateStyle?.titleColor || 'rgba(255, 255, 255, 0.9)',
                marginBottom: {
                  xs: '15px',    // Mobile
                  sm: '18px',    // Tablet
                  md: templateStyle?.titleMarginBottom || '20px'  // Desktop
                },
                fontFamily: templateStyle?.fontFamily,
                textTransform: 'uppercase'
              }}
            >
              {generatedPost.userProfile.title}
            </Typography>

            {/* Testimonial Text */}
            <Typography
              sx={{
                fontSize: {
                  xs: '13px',    // Mobile
                  sm: '14px',    // Tablet
                  md: templateStyle?.textFontSize || '15px'  // Desktop
                },
                lineHeight: templateStyle?.textLineHeight || 1.6,
                marginBottom: {
                  xs: '20px',    // Mobile
                  sm: '22px',    // Tablet
                  md: templateStyle?.textMarginBottom || '25px'  // Desktop
                },
                color: templateStyle?.textColor || '#ffffff',
                fontFamily: templateStyle?.fontFamily,
                wordBreak: 'break-word',
                px: {
                  xs: 1,         // Mobile extra padding
                  sm: 2,         // Tablet extra padding
                  md: 0          // Desktop no extra padding
                }
              }}
            >
              {generatedPost.testimonialText}
            </Typography>

            {/* Star Rating */}
            <Box
              sx={{
                color: templateStyle?.starColor || '#f5a623',
                fontSize: {
                  xs: '18px',    // Mobile
                  sm: '20px',    // Tablet
                  md: templateStyle?.starSize || '22px'  // Desktop
                },
                display: 'flex',
                justifyContent: 'center',
                gap: '2px',
                marginBottom: {
                  xs: '25px',    // Mobile
                  sm: '30px',    // Tablet
                  md: templateStyle?.starMarginBottom || '35px'  // Desktop
                }
              }}
            >
              {[...Array(Math.round(generatedPost.rating || 5))].map((_, index) => (
                <Box key={index}>★</Box>
              ))}
            </Box>

            {/* LEARN MORE Button */}
            <Button
              variant="contained"
              sx={{
                background: templateStyle?.buttonBackground || '#3ad1d6',
                color: templateStyle?.buttonColor || '#003c66',
                padding: {
                  xs: '10px 28px',   // Mobile
                  sm: '11px 32px',   // Tablet
                  md: templateStyle?.buttonPadding || '12px 35px'  // Desktop
                },
                borderRadius: templateStyle?.buttonRadius || '30px',
                fontWeight: templateStyle?.buttonFontWeight || 600,
                fontFamily: templateStyle?.fontFamily,
                fontSize: {
                  xs: '13px',    // Mobile
                  sm: '14px',    // Tablet
                  md: '15px'     // Desktop
                },
                textTransform: 'uppercase',
                boxShadow: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: templateStyle?.buttonBackground || '#3ad1d6',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(58, 209, 214, 0.4)'
                }
              }}
            >
              {templateStyle?.buttonText || 'LEARN MORE'}
            </Button>
          </Box>
        </Box>
      </PreviewContainer>
    );
  }

  // Render other layouts
  return (
    <PreviewContainer
      templateStyle={templateStyle}
      layout={layout}
      className="post-preview-container"
    >
      <ContentCard templateStyle={templateStyle} layout={layout}>
        <QuoteIcon templateStyle={templateStyle} layout={layout} />

        <TitleSection layout={layout}>
          <MainTitle templateStyle={templateStyle} layout={layout}>
            {generatedPost.title}
          </MainTitle>
          <SubTitle templateStyle={templateStyle} layout={layout}>
            {generatedPost.subtitle}
          </SubTitle>
        </TitleSection>

        <ProfileSection layout={layout}>
          <ProfileAvatar
            templateStyle={templateStyle}
            layout={layout}
            src={generatedPost.userProfile.avatar}
            alt={generatedPost.userProfile.name}
            imgProps={defaultAvatarImgProps}
          />
          <Box>
            <ProfileName templateStyle={templateStyle} layout={layout}>
              {generatedPost.userProfile.name}
            </ProfileName>
            <ProfileTitle templateStyle={templateStyle} layout={layout}>
              {generatedPost.userProfile.title}
            </ProfileTitle>
          </Box>
        </ProfileSection>

        <TestimonialText templateStyle={templateStyle} layout={layout}>
          "{generatedPost.testimonialText}"
        </TestimonialText>

        <StyledRating
          templateStyle={templateStyle}
          layout={layout}
          value={generatedPost.rating}
          readOnly
          size={layout === 'professional' ? 'medium' : 'small'}
        />
      </ContentCard>
    </PreviewContainer>
  );
};

export default EnhancedPostPreview;
